import { useState, useCallback } from "react";
import { Droplets, Upload, Download, Trash2, FileText, Loader2, Type, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is this PDF watermark tool free?",
    answer: "Yes, completely free with no signup. Add watermarks to unlimited PDFs."
  },
  {
    question: "What types of watermarks can I add?",
    answer: "You can add text watermarks (like 'CONFIDENTIAL' or 'DRAFT') or upload an image (like your logo) as a watermark."
  },
  {
    question: "Can I customize the watermark appearance?",
    answer: "Yes, you can adjust position (diagonal, center, corners), opacity (transparency), and for text watermarks, the content itself."
  },
  {
    question: "Will the watermark appear on all pages?",
    answer: "Yes, the watermark is applied to every page of your PDF document."
  },
  {
    question: "Are my documents secure?",
    answer: "Yes, all files are automatically deleted after 30 minutes. We never store your documents."
  }
];

const PdfWatermarkPage = () => {
  const [file, setFile] = useState(null);
  const [watermarkType, setWatermarkType] = useState("text");
  const [watermarkText, setWatermarkText] = useState("CONFIDENTIAL");
  const [watermarkImage, setWatermarkImage] = useState(null);
  const [position, setPosition] = useState("diagonal");
  const [opacity, setOpacity] = useState([30]);
  const [fontSize, setFontSize] = useState([48]);
  const [color, setColor] = useState("#808080");
  const [pages, setPages] = useState("all");
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleImageSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      setWatermarkImage(selectedFile);
    } else {
      toast.error("Please upload an image file");
    }
  };

  const handleApplyWatermark = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (watermarkType === "text" && !watermarkText.trim()) {
      toast.error("Please enter watermark text");
      return;
    }

    if (watermarkType === "image" && !watermarkImage) {
      toast.error("Please upload a watermark image");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("position", position);
    formData.append("opacity", opacity[0] / 100);
    formData.append("font_size", fontSize[0]);
    formData.append("color", color.replace("#", ""));
    formData.append("pages", pages);

    if (watermarkType === "text") {
      formData.append("watermark_text", watermarkText);
    } else if (watermarkImage) {
      formData.append("watermark_image", watermarkImage);
    }

    try {
      const response = await axios.post(`${API}/pdf-watermark`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Watermark failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "watermarked.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setWatermarkImage(null);
  };

  return (
    <>
      <SEOHead
        title="Add Watermark to PDF Free Online | Text & Image"
        description="Add text or image watermarks to PDF files. Customize position and opacity. Free online PDF watermarking tool. No signup required."
        canonicalPath="/pdf-watermark"
        keywords="watermark PDF, add watermark to PDF, PDF watermark free, stamp PDF, logo on PDF"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <Droplets className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Add Watermark to PDF Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Add text or image watermarks to your PDF documents. Customize position, opacity, and style. 
              Perfect for branding or marking confidential documents. No signup required.
            </p>
          </div>

        {/* Upload & Options */}
        {!result && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#FF3B30]" />
                  </div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Watermark Options */}
            <div className="border border-border p-6 space-y-6">
              <Tabs value={watermarkType} onValueChange={setWatermarkType}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="text" className="flex items-center gap-1">
                    <Type className="w-4 h-4" /> Text Watermark
                  </TabsTrigger>
                  <TabsTrigger value="image" className="flex items-center gap-1">
                    <ImageIcon className="w-4 h-4" /> Image Watermark
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="text" className="space-y-4 mt-4">
                  <div>
                    <Label>Watermark Text</Label>
                    <Input
                      value={watermarkText}
                      onChange={(e) => setWatermarkText(e.target.value)}
                      placeholder="CONFIDENTIAL"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Font Size: {fontSize[0]}px</Label>
                    <Slider
                      value={fontSize}
                      onValueChange={setFontSize}
                      min={12}
                      max={120}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Color</Label>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-full h-10 cursor-pointer mt-1"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="image" className="space-y-4 mt-4">
                  <div
                    className="border-2 border-dashed border-border p-6 text-center cursor-pointer hover:bg-secondary"
                    onClick={() => document.getElementById("watermark-image").click()}
                  >
                    <input
                      id="watermark-image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    {watermarkImage ? (
                      <p className="text-sm">{watermarkImage.name}</p>
                    ) : (
                      <p className="text-muted-foreground">Click to upload watermark image</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              <div>
                <Label>Position</Label>
                <RadioGroup
                  value={position}
                  onValueChange={setPosition}
                  className="flex gap-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="center" id="center" />
                    <Label htmlFor="center">Center</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="diagonal" id="diagonal" />
                    <Label htmlFor="diagonal">Diagonal</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="tile" id="tile" />
                    <Label htmlFor="tile">Tile</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Opacity: {opacity[0]}%</Label>
                <Slider
                  value={opacity}
                  onValueChange={setOpacity}
                  min={10}
                  max={100}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Apply to Pages</Label>
                <Input
                  value={pages}
                  onChange={(e) => setPages(e.target.value)}
                  placeholder="all or 1,2,3 or 1-5"
                  className="mt-1"
                />
              </div>
            </div>

            <Button
              onClick={handleApplyWatermark}
              disabled={!file || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="apply-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Adding Watermark...
                </>
              ) : (
                <>
                  <Droplets className="w-5 h-5 mr-2" />
                  Add Watermark
                </>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <Droplets className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                Watermark Added
              </h2>
              <p className="text-muted-foreground mb-6">{result.message}</p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={handleReset} className="rounded-sm">
                  <Trash2 className="w-4 h-4 mr-2" /> Process Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-watermark" content={toolContent["pdf-watermark"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-watermark" />
      </div>
    </div>
    </>
  );
};

export default PdfWatermarkPage;
